import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from '../store';
import {
  fetchMyUpcomingGames,
  fetchProposals,
  fetchMyRecentResults,
  acceptProposal,
} from '../store/slices/gameSlice';
import { fetchLeagueSummary } from '../store/slices/leagueSlice';
import {
  fetchMyTeams,
  fetchPartnerRequests,
  acceptPartnerRequest,
  declinePartnerRequest,
} from '../store/slices/teamSlice';
import { GameProposalStatus } from '@sulis-liiga/common';
import GameSummaryRow, { toGameRowProps } from '../components/game/GameSummaryRow';
import { proposalToRowInput, summaryToRowInput, type BannerContext } from '../utils/gameBanner';
import { canRespondToProposal } from '../utils/proposals';

const HomePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const gameState = useSelector((state: RootState) => state.game);
  const { myUpcomingGames, myRecentResults, proposals, loading: gamesLoading } = gameState;
  const { selectedLeagueId, selectedTenantSlug, bannerLeagues } = useSelector(
    (state: RootState) => state.ui,
  );
  const { summary } = useSelector((state: RootState) => state.league);
  const { myTeams, partnerRequests } = useSelector((state: RootState) => state.team);

  const selectedLeague =
    bannerLeagues.find(l => l.leagueId === selectedLeagueId) ?? bannerLeagues[0] ?? null;

  // Fetch the per-league summary stats (players / teams / games / season) for the
  // snapshot. Public endpoint — works logged out.
  useEffect(() => {
    if (selectedLeague?.leagueId) {
      dispatch(fetchLeagueSummary(selectedLeague.leagueId));
    }
  }, [dispatch, selectedLeague?.leagueId]);

  // The 4-card snapshot (same for logged-in and logged-out), scoped to the chosen
  // league. Defined once and reused in both branches below.
  const snapshot = (
    <section className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-5">
      <div className="bg-white rounded-lg shadow-sm p-5">
        <div className="text-sm text-gray-500">Players</div>
        <div className="mt-1 text-2xl font-bold">{summary ? summary.players : '—'}</div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-5">
        <div className="text-sm text-gray-500">Teams</div>
        <div className="mt-1 text-2xl font-bold">{summary ? summary.teams : '—'}</div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-5">
        <div className="text-sm text-gray-500">Games</div>
        <div className="mt-1 text-2xl font-bold">{summary ? summary.games : '—'}</div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-5">
        <div className="text-sm text-gray-500">Season</div>
        {summary?.season ? (
          <div className="mt-1 text-lg font-semibold">
            {summary.season.name}{' '}
            <span
              className={`ml-1 align-middle text-xs px-2 py-0.5 rounded-full capitalize ${
                summary.season.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {summary.season.status}
            </span>
          </div>
        ) : (
          <div className="mt-1 text-lg font-semibold text-gray-400">—</div>
        )}
      </div>
    </section>
  );

  // Fetch cross-tenant upcoming games and recent results for authenticated users
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchMyUpcomingGames('scheduled'));
      dispatch(fetchMyRecentResults());
    }
  }, [dispatch, isAuthenticated]);

  // Load the player's proposals (scoped server-side to their teams) so we can
  // show the pending ones. Routed by the active tenant slug.
  useEffect(() => {
    if (isAuthenticated && selectedTenantSlug) {
      dispatch(fetchProposals(undefined));
    }
  }, [dispatch, isAuthenticated, selectedTenantSlug]);

  const pendingProposals = (proposals ?? []).filter(
    p => p.status === GameProposalStatus.PENDING || p.status === GameProposalStatus.COUNTERED,
  );

  // Viewer context for the shared banner builder (so a proposal on Home renders
  // the same status label + Approve action as on the Games page).
  const myTeamIds = useMemo(() => new Set(myTeams.map(t => t.id)), [myTeams]);
  const bannerCtx: BannerContext = useMemo(
    () => ({ myTeamIds, userId: user?.id }),
    [myTeamIds, user?.id],
  );

  // One-click approval of a single-slot proposal (accepts the only slot and lands
  // on the scheduled game); multi-slot proposals link to the proposal detail.
  const handleApproveProposal = async (proposalId: string) => {
    try {
      const accepted = await dispatch(acceptProposal({ proposalId, slotIndex: 0 })).unwrap();
      navigate(accepted.scheduledGameId ? `/games/${accepted.scheduledGameId}` : '/games');
    } catch {
      // The proposal detail page surfaces errors; nothing inline here.
    }
  };

  // The three most recent completed games (the API returns them ascending by date).
  const recentResults = [...(myRecentResults ?? [])]
    .sort((a, b) => new Date(b.scheduledDate).getTime() - new Date(a.scheduledDate).getTime())
    .slice(0, 3);

  // Load the player's teams for the active tenant so we can tell whether they have
  // already joined the selected league. getMyTeams() is routed by the active tenant
  // slug, so re-fetch whenever the banner tenant changes.
  useEffect(() => {
    if (isAuthenticated && selectedTenantSlug) {
      dispatch(fetchMyTeams());
    }
  }, [dispatch, isAuthenticated, selectedTenantSlug]);

  // Load incoming partner (team) invitations awaiting this player's response, so
  // they surface in "My pending actions" alongside proposals and results.
  useEffect(() => {
    if (isAuthenticated && selectedTenantSlug) {
      dispatch(fetchPartnerRequests());
    }
  }, [dispatch, isAuthenticated, selectedTenantSlug]);

  const handleAcceptPartner = (teamId: string) => {
    dispatch(acceptPartnerRequest(teamId));
  };
  const handleDeclinePartner = (teamId: string) => {
    dispatch(declinePartnerRequest(teamId));
  };

  // "My pending actions" aggregates the three things that need THIS player to act:
  //   1. team/partner invitations to accept or decline,
  //   2. game proposals where it is my team's turn to respond,
  //   3. submitted results awaiting my approval.
  const respondableProposals = pendingProposals.filter(p => canRespondToProposal(p, myTeamIds));
  const resultsToApprove = (myUpcomingGames ?? []).filter(g => g.awaitingMyApproval);
  const pendingActionsCount =
    partnerRequests.length + respondableProposals.length + resultsToApprove.length;

  // Games shown under "My upcoming games" exclude those already surfaced as a
  // pending approval above, so each game appears once.
  const upcomingGamesToShow = (myUpcomingGames ?? []).filter(g => !g.awaitingMyApproval);

  const leagueId = selectedLeague?.leagueId;

  // A player enters the league by creating a team (enrollment is integrated into
  // team creation). Until they have a team, the hero offers "Create Team"; once
  // they do, it offers the day-to-day actions instead.
  const hasTeam = myTeams.length > 0;

  return (
    <div>
      {isAuthenticated && user ? (
        /* ── Logged-in view ──────────────────────────────────────────── */
        <>
          {/* Welcome hero */}
          <section className="bg-white rounded-lg shadow-sm p-8">
            <h1 className="text-3xl font-bold">Welcome, {user.firstName}!</h1>
            <p className="mt-2 text-gray-600 max-w-2xl">
              {selectedLeague
                ? `Here's what's happening in ${selectedLeague.name} this week.`
                : "Here's what's happening this week."}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {!hasTeam ? (
                /* No team yet — creating one (with an integrated division enrollment)
                   is how the player enters the league. */
                <Link
                  to="/teams/new"
                  className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                >
                  Create Team
                </Link>
              ) : (
                <>
                  <Link
                    to={leagueId ? `/leagues/${leagueId}/teams` : '/teams'}
                    className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                  >
                    My Teams
                  </Link>
                  <Link
                    to="/games/proposals/create"
                    className="px-5 py-2.5 rounded-md text-sm font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 transition-colors"
                  >
                    Propose Game
                  </Link>
                  <Link
                    to="/games/record"
                    className="px-5 py-2.5 rounded-md text-sm font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 transition-colors"
                  >
                    Record Game
                  </Link>
                </>
              )}
            </div>
          </section>

          {/* League snapshot */}
          {snapshot}

          {/* My pending actions — invites to accept, proposals to respond to, results to approve */}
          {pendingActionsCount > 0 && (
            <section className="mt-6">
              <h2 className="text-lg font-medium">My pending actions ({pendingActionsCount})</h2>
              <div className="mt-2 space-y-3">
                {/* Team/partner invitations awaiting my response */}
                {partnerRequests.length > 0 && (
                  <div className="bg-white shadow-sm rounded-lg divide-y divide-gray-200">
                    {partnerRequests.map(team => (
                      <div
                        key={team.id}
                        className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Team invitation — {team.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            You&apos;ve been invited to be a partner. Accept to activate the team.
                          </p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleAcceptPartner(team.id)}
                            className="px-4 py-1.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeclinePartner(team.id)}
                            className="px-4 py-1.5 rounded-md text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Game proposals where it is my team's turn to respond */}
                {respondableProposals.length > 0 && (
                  <div className="bg-white shadow-sm rounded-lg divide-y divide-gray-200">
                    {respondableProposals.map(p => (
                      <GameSummaryRow
                        key={p.id}
                        {...toGameRowProps(proposalToRowInput(p, bannerCtx))}
                        onApproveProposal={handleApproveProposal}
                      />
                    ))}
                  </div>
                )}

                {/* Submitted results awaiting my approval */}
                {resultsToApprove.length > 0 && (
                  <div className="bg-white shadow-sm rounded-lg divide-y divide-gray-200">
                    {resultsToApprove.map(game => (
                      <GameSummaryRow
                        key={game.gameId}
                        {...toGameRowProps(summaryToRowInput(game))}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* My upcoming games */}
          <section className="mt-6">
            <h2 className="text-lg font-medium">My upcoming games</h2>
            <div className="mt-2 bg-white shadow-sm rounded-lg divide-y divide-gray-200">
              {gamesLoading ? (
                <div className="px-4 py-8 flex justify-center">
                  <svg
                    className="animate-spin h-8 w-8 text-primary-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              ) : upcomingGamesToShow.length > 0 ? (
                upcomingGamesToShow.map(game => (
                  <GameSummaryRow key={game.gameId} {...toGameRowProps(summaryToRowInput(game))} />
                ))
              ) : (
                <div className="px-4 py-4 text-sm text-gray-500">
                  No upcoming games.{' '}
                  <Link to="/games/proposals/create" className="text-primary-600 font-medium">
                    Propose a game
                  </Link>
                </div>
              )}
            </div>
          </section>

          {/* My recent results */}
          {recentResults.length > 0 && (
            <section className="mt-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium">My recent results</h2>
                <Link
                  to={leagueId ? `/leagues/${leagueId}/games?tab=results` : '/games'}
                  className="text-sm text-primary-600 font-medium"
                >
                  More results &rarr;
                </Link>
              </div>
              <div className="mt-2 bg-white shadow-sm rounded-lg divide-y divide-gray-200">
                {recentResults.map(game => (
                  <GameSummaryRow key={game.gameId} {...toGameRowProps(summaryToRowInput(game))} />
                ))}
              </div>
            </section>
          )}

          {/* Quick links */}
          <section className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
            <Link
              to={leagueId ? `/leagues/${leagueId}/divisions` : '/leagues'}
              className="bg-white rounded-lg shadow-sm p-5 hover:shadow-md transition-shadow"
            >
              <div className="font-semibold">Divisions</div>
              <div className="text-sm text-gray-500 mt-1">Standings across the five categories</div>
              <div className="mt-3 text-sm text-primary-600 font-medium">Explore &rarr;</div>
            </Link>
            <Link
              to={leagueId ? `/leagues/${leagueId}/teams` : '/teams'}
              className="bg-white rounded-lg shadow-sm p-5 hover:shadow-md transition-shadow"
            >
              <div className="font-semibold">Teams</div>
              <div className="text-sm text-gray-500 mt-1">Manage your teams</div>
              <div className="mt-3 text-sm text-primary-600 font-medium">View teams &rarr;</div>
            </Link>
            <Link
              to={leagueId ? `/leagues/${leagueId}/games` : '/games'}
              className="bg-white rounded-lg shadow-sm p-5 hover:shadow-md transition-shadow"
            >
              <div className="font-semibold">Games</div>
              <div className="text-sm text-gray-500 mt-1">Schedule &amp; results</div>
              <div className="mt-3 text-sm text-primary-600 font-medium">View games &rarr;</div>
            </Link>
          </section>
        </>
      ) : (
        /* ── Logged-out view ─────────────────────────────────────────── */
        <>
          {/* Public hero */}
          <section className="bg-white rounded-lg shadow-sm p-8">
            <h1 className="text-3xl font-bold">Welcome to Sulis-Liiga</h1>
            <p className="mt-2 text-gray-600 max-w-2xl">
              The badminton league for singles and doubles play — register a team, climb your
              division, and track every game.{' '}
              {selectedLeague ? (
                <>
                  Browse the <strong>{selectedLeague.name}</strong> league below — no account
                  needed.
                </>
              ) : (
                'Browse leagues below — no account needed.'
              )}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/register"
                className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
              >
                Sign up
              </Link>
              <Link
                to="/how-it-works"
                className="px-5 py-2.5 rounded-md text-sm font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 transition-colors"
              >
                See how it works
              </Link>
              <Link
                to="/login"
                className="px-5 py-2.5 rounded-md text-sm font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 transition-colors"
              >
                Log in
              </Link>
              <Link
                to="/login?returnTo=/teams/new"
                className="px-5 py-2.5 rounded-md text-sm font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 transition-colors"
              >
                Create Team
              </Link>
            </div>
          </section>

          {/* League snapshot */}
          {snapshot}

          {/* Login hint */}
          <section className="mt-6">
            <div className="bg-white shadow-sm rounded-lg px-4 py-4">
              <p className="text-sm text-gray-500">
                Log in to join a league.{' '}
                <Link to="/login" className="text-primary-600 font-medium">
                  Log in &rarr;
                </Link>
              </p>
            </div>
          </section>

          {/* Quick links */}
          <section className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
            <Link
              to={leagueId ? `/leagues/${leagueId}/divisions` : '/leagues'}
              className="bg-white rounded-lg shadow-sm p-5 hover:shadow-md transition-shadow"
            >
              <div className="font-semibold">Divisions</div>
              <div className="text-sm text-gray-500 mt-1">Standings across the five categories</div>
              <div className="mt-3 text-sm text-primary-600 font-medium">Explore &rarr;</div>
            </Link>
            <Link
              to={leagueId ? `/leagues/${leagueId}/teams` : '/teams'}
              className="bg-white rounded-lg shadow-sm p-5 hover:shadow-md transition-shadow"
            >
              <div className="font-semibold">Teams</div>
              <div className="text-sm text-gray-500 mt-1">Who's registered this season</div>
              <div className="mt-3 text-sm text-primary-600 font-medium">View teams &rarr;</div>
            </Link>
            <Link
              to={leagueId ? `/leagues/${leagueId}/games` : '/games'}
              className="bg-white rounded-lg shadow-sm p-5 hover:shadow-md transition-shadow"
            >
              <div className="font-semibold">Games</div>
              <div className="text-sm text-gray-500 mt-1">Schedule &amp; results</div>
              <div className="mt-3 text-sm text-primary-600 font-medium">View games &rarr;</div>
            </Link>
          </section>
        </>
      )}

      {/* New-player onboarding link — bottom of Home for every visitor */}
      <section className="mt-6">
        <div className="bg-white rounded-lg shadow-sm px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-gray-600">
            New to Sulis-Liiga? See how the league works in three simple steps.
          </p>
          <Link to="/how-it-works" className="text-sm text-primary-600 font-medium shrink-0">
            How it works &rarr;
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
